import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Clock, ListChecks, Trophy, Target, PlayCircle, ArrowRight, Loader2, Tag, Pencil, Library, Sparkles, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AppHeader } from "@/components/AppHeader";
import { StatCard } from "@/components/StatCard";
import { CategoryPickerModal } from "@/components/CategoryPickerModal";
import { SignupPaymentBanner } from "@/components/SignupPaymentBanner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate, duration } from "@/lib/api";

const TestCard = ({ t, i, onStart, starting, disabled }) => (
  <div className="fade-up group bg-white border border-slate-200 rounded-xl p-6 flex flex-col hover:border-[#2563EB] transition-colors"
    style={{ animationDelay: `${i * 60}ms` }} data-testid={`student-test-card-${t.id}`}>
    <div className="flex items-center justify-between gap-2">
      <div className="flex flex-wrap gap-1.5">
        {(t.categories && t.categories.length > 0) ? t.categories.map((c) => (
          <Badge key={c} className="bg-[#EFF6FF] text-[#1D4ED8] hover:bg-[#EFF6FF] font-mono text-[10px] uppercase">{c}</Badge>
        )) : <Badge variant="outline" className="font-mono text-[10px] uppercase">Open</Badge>}
      </div>
      {t.attempts > 0 && <span className="text-xs text-slate-500 whitespace-nowrap">{t.attempts} attempt{t.attempts > 1 ? "s" : ""}</span>}
    </div>
    <h3 className="font-heading text-lg font-semibold text-slate-900 mt-4">{t.title}</h3>
    <p className="text-sm text-slate-500 mt-1.5 line-clamp-2 flex-1">{t.description || "No description"}</p>
    <div className="flex gap-4 mt-5 text-sm text-slate-600">
      <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" />{t.duration_minutes} min</span>
      <span className="flex items-center gap-1.5"><ListChecks className="h-4 w-4" />{t.question_count} Qs</span>
      <span className="flex items-center gap-1.5"><Target className="h-4 w-4" />{t.pass_percentage}%</span>
    </div>
    <Button onClick={() => onStart(t)} disabled={starting === t.id || disabled} data-testid={`start-test-button-${t.id}`}
      className="mt-5 bg-slate-900 hover:bg-[#2563EB] hover:text-white text-white transition-colors">
      {starting === t.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-2" />}
      {disabled ? "Suspended" : t.in_progress_attempt_id ? "Resume Test" : t.attempts ? "Retake Test" : "Start Test"}
    </Button>
  </div>
);

export default function StudentDashboard() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [tests, setTests] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [starting, setStarting] = useState(null);
  const [showPicker, setShowPicker] = useState(false);

  const loadTests = () => Promise.all([api.get("/student/tests"), api.get("/student/attempts")])
    .then(([t, a]) => { setTests(t.data); setAttempts(a.data); })
    .catch((e) => toast.error(errMsg(e)));

  useEffect(() => {
    if (user && !user.category) setShowPicker(true);
    loadTests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async (t) => {
    setStarting(t.id);
    try {
      const { data } = await api.post(`/student/tests/${t.id}/start`);
      nav(`/test/${data.attempt_id}`);
    } catch (e) {
      toast.error(errMsg(e));
      setStarting(null);
    }
  };

  const onCategorySaved = (updated) => {
    setUser(updated);
    setShowPicker(false);
    loadTests();
  };

  const avg = attempts.length ? Math.round(attempts.reduce((s, a) => s + a.percentage, 0) / attempts.length) : 0;
  const best = attempts.length ? Math.round(Math.max(...attempts.map((a) => a.percentage))) : 0;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <AppHeader />
      <main className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-10">
        <div className="fade-up">
          <div className="small-label">Student Dashboard</div>
          <div className="flex flex-wrap items-end justify-between gap-4 mt-2">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight" data-testid="dashboard-welcome">
              Hello, {user?.name?.split(" ")[0]}.
            </h1>
            <div className="flex flex-wrap items-center gap-2">
              {user?.library_name && (
                <div data-testid="dashboard-library-badge"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white border border-slate-200">
                  <Library className="h-4 w-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-900">{user.library_name}</span>
                </div>
              )}
              {user?.category && (
                <button type="button" onClick={() => setShowPicker(true)} data-testid="dashboard-category-badge"
                  className="group inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white border border-slate-200 hover:border-[#2563EB] transition-colors">
                  <Tag className="h-4 w-4 text-[#1D4ED8]" />
                  <span className="text-sm font-medium text-slate-900">Preparing for: {user.category}</span>
                  <Pencil className="h-3.5 w-3.5 text-slate-400 group-hover:text-[#1D4ED8]" />
                </button>
              )}
            </div>
          </div>
          <div className="mt-5 bg-gradient-to-r from-[#0F172A] to-[#1E3A8A] text-white rounded-xl px-5 py-4 flex items-start gap-3" data-testid="dashboard-faculty-banner">
            <Sparkles className="h-5 w-5 text-amber-300 shrink-0 mt-0.5" />
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-300">Faculty</div>
              <p className="font-heading text-base sm:text-lg font-semibold leading-snug mt-0.5">
                These mock tests are designed by professionals from Aakash Institute and Unacademy.
              </p>
            </div>
          </div>
          {user?.inactive_reason && <SignupPaymentBanner reason={user.inactive_reason} />}
          <p className="text-slate-500 mt-4">Pick a test below. The timer starts the moment you begin.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10">
          <StatCard label="Tests Completed" value={attempts.length} icon={ListChecks} testId="stat-completed" />
          <StatCard label="Average Score" value={`${avg}%`} icon={Target} testId="stat-average" delay={60} />
          <StatCard label="Best Score" value={`${best}%`} icon={Trophy} testId="stat-best" delay={120} />
        </div>

        <section className="mt-14">
          <h2 className="font-heading text-lg font-semibold">Available Tests {user?.category && <span className="text-slate-400 font-normal">for {user.category}</span>}</h2>
          {tests === null ? (
            <div className="py-16 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : tests.length === 0 ? (
            <div className="mt-4 border border-dashed border-slate-300 rounded-xl p-10 text-center text-slate-500" data-testid="no-tests-message">
              {user?.category
                ? <>No tests available for <span className="font-medium text-slate-700">{user.category}</span> yet. Check back soon.</>
                : "Pick your exam category above to see your tests."}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-4">
              {tests.map((t, i) => <TestCard key={t.id} t={t} i={i} onStart={start} starting={starting} disabled={!!user?.inactive_reason} />)}
            </div>
          )}
        </section>

        <section className="mt-14">
          <h2 className="font-heading text-lg font-semibold">Past Attempts</h2>
          <div className="mt-4 bg-white border border-slate-200 rounded-xl overflow-x-auto">
            <Table data-testid="past-attempts-table">
              <TableHeader>
                <TableRow><TableHead>Test</TableHead><TableHead>Date</TableHead><TableHead>Score</TableHead>
                  <TableHead>Time Taken</TableHead><TableHead>Status</TableHead><TableHead /></TableRow>
              </TableHeader>
              <TableBody>
                {attempts.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-10">No attempts yet.</TableCell></TableRow>
                )}
                {attempts.map((a) => (
                  <TableRow key={a.id} data-testid={`attempt-row-${a.id}`}>
                    <TableCell className="font-medium">{a.test_title}</TableCell>
                    <TableCell className="text-slate-500">{fmtDate(a.submitted_at)}</TableCell>
                    <TableCell className="font-mono">{a.score}/{a.total} · {Math.round(a.percentage)}%</TableCell>
                    <TableCell className="text-slate-500">{duration(a.started_at, a.submitted_at)}</TableCell>
                    <TableCell>
                      <Badge className={a.passed ? "bg-green-100 text-green-800 hover:bg-green-100" : "bg-red-100 text-red-800 hover:bg-red-100"}>
                        {a.passed ? "Passed" : "Failed"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => nav(`/result/${a.id}`)} data-testid={`review-result-button-${a.id}`}
                        className="hover:bg-slate-900 hover:text-white">Review<ArrowRight className="h-4 w-4 ml-1" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      </main>
      <CategoryPickerModal open={showPicker} onSaved={onCategorySaved} />
    </div>
  );
}
